import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';

import IdsElement from '../../core/ids-element';
import IdsFitViewportMixin from '../../mixins/ids-fit-viewport-mixin/ids-fit-viewport-mixin';

import styles from './ids-layout-flex.scss';

import './ids-layout-flex-item';

const CENTER_INPUTS_SELECTOR = 'ids-input, ids-date-picker, ids-time-picker, ids-dropdown, ids-textarea, ids-trigger-field';

// List of flex options
export const FLEX_OPTIONS = {
  alignContent: ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly', 'stretch', 'start', 'end', 'baseline'],
  alignItems: ['start', 'end', 'flex-start', 'flex-end', 'center', 'center-inputs', 'baseline', 'stretch'],
  direction: ['row', 'row-reverse', 'column', 'column-reverse'],
  display: ['flex', 'inline-flex'],
  justifyContent: ['start', 'stretch', 'end', 'flex-start', 'flex-end', 'center', 'left', 'right', 'space-between', 'space-around', 'space-evenly'],
  wrap: ['nowrap', 'wrap', 'wrap-reverse'],
  units: [0, 1, 2, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40],
  // Named spacing tokens mirror ids-layout-grid so the two components are interchangeable.
  gapSizes: ['none', 'xs', 'sm', 'md', 'lg', 'xl'],
  paddingSizes: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'],
  marginSizes: ['xs', 'sm', 'md', 'lg', 'xl', 'auto']
};

type ButtonAlignment = {
  buttonItem: HTMLElement | null;
  offset: number;
  wrapped: boolean;
};

const findVisibleFieldContainer = (root: Element | ShadowRoot): HTMLElement | null => {
  const elements = root.querySelectorAll<HTMLElement>('*');
  for (const element of elements) {
    if (element.classList.contains('field-container')) {
      const box = element.getBoundingClientRect();
      if (box.width || box.height) return element;
    }
    if (element.shadowRoot) {
      const fieldContainer = findVisibleFieldContainer(element.shadowRoot);
      if (fieldContainer) return fieldContainer;
    }
  }
  return null;
};

// Batch center-input measurements into one read phase and one write phase.
const wrapBatch = new Set<IdsLayoutFlex>();
let wrapBatchRaf = 0;

const flushWrapBatch = () => {
  wrapBatchRaf = 0;
  const pending = [...wrapBatch];
  wrapBatch.clear();
  // Read phase: measure everything first so no write has dirtied layout yet.
  const results = pending.map((el) => el.measureButtonWrap());
  // Write phase: apply class changes after all reads are done.
  pending.forEach((el, i) => el.applyButtonWrap(results[i]));
};

const scheduleWrapBatch = (el: IdsLayoutFlex) => {
  wrapBatch.add(el);
  if (!wrapBatchRaf) wrapBatchRaf = requestAnimationFrame(flushWrapBatch);
};

const Base = IdsFitViewportMixin(
  IdsElement
);

/**
 * IDS Layout Flex Component
 * @type {IdsLayoutFlex}
 * @mixes IdsFitViewportMixin
 * @inherits IdsElement
 */
@customElement('ids-layout-flex')
@scss(styles)
export default class IdsLayoutFlex extends Base {
  constructor() {
    super();
  }

  static get attributes(): any {
    return [
      ...super.attributes,
      attributes.ALIGN_CONTENT,
      attributes.ALIGN_ITEMS,
      attributes.DIRECTION,
      attributes.DISPLAY,
      attributes.FILL,
      attributes.FIT_VIEWPORT,
      attributes.GAP,
      attributes.GAP_X,
      attributes.GAP_Y,
      attributes.HEIGHT,
      attributes.JUSTIFY_CONTENT,
      attributes.MARGIN,
      attributes.MARGIN_Y,
      attributes.PADDING,
      attributes.PADDING_X,
      attributes.PADDING_Y,
      attributes.WRAP
    ];
  }

  #childObserver?: MutationObserver;

  #resizeObserver?: ResizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#syncChildFieldHeight();
    this.#syncButtonWrap();
    this.#observeChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#childObserver?.disconnect();
    this.#childObserver = undefined;
    this.#disconnectResize();
    wrapBatch.delete(this);
  }

  /**
   * Sync field sizing and top-label state for center-inputs alignment.
   */
  #syncChildFieldHeight(): void {
    const current = this.getAttribute(attributes.FIELD_HEIGHT);
    const directItems = Array.from(this.querySelectorAll<HTMLElement>(':scope > ids-layout-flex-item'));
    const clearCenterInputLabelGaps = (): void => {
      directItems.forEach((item) => {
        item.querySelector<HTMLElement>(CENTER_INPUTS_SELECTOR)
          ?.style.removeProperty('--ids-center-inputs-label-gap');
      });
    };

    if (this.alignItems !== 'center-inputs') {
      if (current !== null) this.removeAttribute(attributes.FIELD_HEIGHT);
      this.classList.remove('has-top-label-field');
      clearCenterInputLabelGaps();
      return;
    }

    const inputs = this.querySelectorAll<HTMLElement>(CENTER_INPUTS_SELECTOR);
    const fieldHeightOrder = ['xs', 'sm', 'md', 'lg'] as const;
    type FieldHeight = typeof fieldHeightOrder[number];
    const getFieldHeightVariable = (fieldHeight: FieldHeight | null): string | null => {
      switch (fieldHeight) {
        case 'xs':
          return 'var(--ids-input-height-10, 0px)';
        case 'sm':
          return 'var(--ids-input-height-20, 0px)';
        case 'md':
          return 'var(--ids-input-height-30, 0px)';
        case 'lg':
          return 'var(--ids-input-height-40, 0px)';
        default:
          return null;
      }
    };
    const getEffectiveFieldHeight = (el: HTMLElement): FieldHeight => {
      const fieldHeight = el.getAttribute(attributes.FIELD_HEIGHT);
      if (fieldHeight === 'xxs' || el.hasAttribute(attributes.COMPACT)) return 'xs';

      switch (fieldHeight) {
        case 'xs':
        case 'sm':
        case 'md':
        case 'lg':
          return fieldHeight;
        default:
          return 'md';
      }
    };
    let detected: FieldHeight | null = null;
    let hasTopLabel = false;

    inputs.forEach((el) => {
      if (el.hasAttribute('label') && el.getAttribute('label-position') !== 'inline-start') {
        hasTopLabel = true;
      }

      const effectiveHeight = getEffectiveFieldHeight(el);
      if (!detected || fieldHeightOrder.indexOf(effectiveHeight) > fieldHeightOrder.indexOf(detected)) {
        detected = effectiveHeight;
      }
    });

    this.classList.toggle('has-top-label-field', hasTopLabel);

    directItems.forEach((item) => {
      const field = Array.from(item.children).find((element): element is HTMLElement => (
        element instanceof HTMLElement && element.matches(CENTER_INPUTS_SELECTOR)
      ));
      const effectiveHeight = field ? getEffectiveFieldHeight(field) : null;
      const detectedVariable = getFieldHeightVariable(detected);
      const effectiveVariable = getFieldHeightVariable(effectiveHeight);
      if (field && hasTopLabel && effectiveHeight !== detected && detectedVariable && effectiveVariable) {
        field.style.setProperty(
          '--ids-center-inputs-label-gap',
          `calc(var(--ids-input-margin-bottom, 0px) + ((${detectedVariable} - ${effectiveVariable}) / 2))`
        );
      } else {
        field?.style.removeProperty('--ids-center-inputs-label-gap');
      }
    });

    if (detected && detected !== 'md') {
      if (current !== detected) this.setAttribute(attributes.FIELD_HEIGHT, detected);
    } else if (current !== null) {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  /**
   * Turn button alignment measurement on/off for this container.
   */
  #syncButtonWrap(): void {
    if (this.alignItems !== 'center-inputs') {
      this.classList.remove('has-wrapped-button');
      this.querySelectorAll<HTMLElement>(':scope > ids-layout-flex-item')
        .forEach((item) => item.style.removeProperty('--ids-flex-item-offset'));
      this.#disconnectResize();
      wrapBatch.delete(this);
      return;
    }
    this.#observeResize();
    scheduleWrapBatch(this);
  }

  /**
   * Measure the button offset from the preceding interactive field.
   * @returns {ButtonAlignment} measured button alignment
   */
  measureButtonWrap(): ButtonAlignment {
    const emptyResult = { buttonItem: null, offset: 0, wrapped: false };
    if (this.alignItems !== 'center-inputs') return emptyResult;

    const items = [...this.querySelectorAll<HTMLElement>(':scope > ids-layout-flex-item')];
    const buttonIndex = items.findIndex((item) => !!item.querySelector(':scope > ids-button'));
    const buttonItem = items[buttonIndex];
    const fieldItem = items
      .slice(0, buttonIndex)
      .reverse()
      .find((item) => !!item.querySelector(CENTER_INPUTS_SELECTOR));
    const button = buttonItem?.querySelector<HTMLElement>(':scope > ids-button');
    if (!buttonItem || !fieldItem || !button) return emptyResult;

    const buttonBox = button.getBoundingClientRect();
    const fieldItemBox = fieldItem.getBoundingClientRect();
    if ((!buttonBox.width && !buttonBox.height) || (!fieldItemBox.width && !fieldItemBox.height)) {
      return emptyResult;
    }

    // Detect a wrapped button via a direction-aware horizontal edge check plus a direction-agnostic vertical check.
    const isRTL = getComputedStyle(this).direction === 'rtl';
    const wrappedHorizontally = isRTL
      ? buttonBox.right >= fieldItemBox.right
      : buttonBox.left <= fieldItemBox.left;
    const wrapped = this.wrap === 'wrap'
      && (wrappedHorizontally || buttonBox.top >= fieldItemBox.bottom);
    const field = fieldItem.querySelector<HTMLElement>(CENTER_INPUTS_SELECTOR);
    const isTextarea = field?.tagName === 'IDS-TEXTAREA';
    const textarea = field as (HTMLElement & { fieldContainer?: HTMLElement | null }) | null;
    const fieldContainer = isTextarea ? textarea?.fieldContainer : findVisibleFieldContainer(fieldItem);
    if (wrapped || !fieldContainer) return { buttonItem, offset: 0, wrapped };

    const fieldBox = fieldContainer.getBoundingClientRect();
    const currentMargin = parseFloat(getComputedStyle(buttonItem).marginBlockStart) || 0;
    const buttonTop = buttonBox.top - currentMargin;
    const offset = isTextarea
      ? fieldBox.top - buttonTop
      : (fieldBox.top + (fieldBox.height / 2)) - (buttonTop + (buttonBox.height / 2));
    return { buttonItem, offset, wrapped };
  }

  /**
   * Apply the measured button alignment.
   * @param {ButtonAlignment} result measured alignment
   */
  applyButtonWrap(result: ButtonAlignment): void {
    this.classList.toggle('has-wrapped-button', result.wrapped);
    this.querySelectorAll<HTMLElement>(':scope > ids-layout-flex-item')
      .forEach((item) => {
        if (item === result.buttonItem) {
          item.style.setProperty('--ids-flex-item-offset', `${result.offset}px`);
        } else {
          item.style.removeProperty('--ids-flex-item-offset');
        }
      });
  }

  #observeResize(): void {
    if (this.#resizeObserver) return;
    this.#resizeObserver = new ResizeObserver(() => scheduleWrapBatch(this));
    this.#resizeObserver.observe(this);
  }

  #disconnectResize(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  #observeChildren(): void {
    this.#childObserver = new MutationObserver((records) => {
      // Ignore attribute mutations on the host itself to avoid feedback loops
      // when we reflect the inferred field-height back onto this element.
      // But always process childList mutations (even on `this`) since they
      // indicate children being added/removed.
      const relevant = records.some((r) => r.type === 'childList' || r.target !== this);
      if (relevant) {
        this.#syncChildFieldHeight();
        this.#syncButtonWrap();
      }
    });
    this.#childObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [attributes.FIELD_HEIGHT, attributes.COMPACT, attributes.LABEL_POSITION]
    });
  }

  template(): string {
    return `<slot></slot>`;
  }

  /**
   * Set the align content setting
   * @param {string} value The value
   */
  set alignContent(value: string | null) {
    if (value && FLEX_OPTIONS.alignContent.includes(value)) {
      this.setAttribute(attributes.ALIGN_CONTENT, value);
    } else {
      this.removeAttribute(attributes.ALIGN_CONTENT);
    }
  }

  get alignContent() { return this.getAttribute(attributes.ALIGN_CONTENT); }

  /**
   * Set the align items setting
   * @param {string} value The value
   */
  set alignItems(value: string | null) {
    if (value && FLEX_OPTIONS.alignItems.includes(value)) {
      this.setAttribute(attributes.ALIGN_ITEMS, value);
    } else {
      this.removeAttribute(attributes.ALIGN_ITEMS);
    }
    this.#syncChildFieldHeight();
    this.#syncButtonWrap();
  }

  get alignItems() { return this.getAttribute(attributes.ALIGN_ITEMS); }

  /**
   * Set the direction setting
   * @param {string} value The value
   */
  set direction(value: string | null) {
    if (value && FLEX_OPTIONS.direction.includes(value)) {
      this.setAttribute(attributes.DIRECTION, value);
    } else {
      this.removeAttribute(attributes.DIRECTION);
    }
  }

  get direction() { return this.getAttribute(attributes.DIRECTION); }

  /**
   * Set the display setting
   * @param {string} value The value
   */
  set display(value: string | null) {
    if (value && FLEX_OPTIONS.display.includes(value)) {
      this.setAttribute(attributes.DISPLAY, value);
    } else {
      this.removeAttribute(attributes.DISPLAY);
    }
  }

  get display() { return this.getAttribute(attributes.DISPLAY); }

  /**
   * Validates a gap value (named token or pixel unit) and writes/removes the attribute.
   * @param {string} attr The attribute name
   * @param {number | string | null} value The gap value to validate and set
   */
  #setGapAttribute(attr: string, value: number | string | null): void {
    if (typeof value === 'string' && FLEX_OPTIONS.gapSizes.includes(value)) {
      this.setAttribute(attr, value);
      return;
    }
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && FLEX_OPTIONS.units.includes(val)) {
      this.setAttribute(attr, String(val));
    } else {
      this.removeAttribute(attr);
    }
  }

  /**
   * Set the gap, apply same for both horizontal and vertical sides
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gap(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP, value);
  }

  get gap() { return this.getAttribute(attributes.GAP); }

  /**
   * Set the horizontal gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapX(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_X, value);
  }

  get gapX() { return this.getAttribute(attributes.GAP_X); }

  /**
   * Set card vertical gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapY(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_Y, value);
  }

  get gapY() { return this.getAttribute(attributes.GAP_Y); }

  /**
   * Set the padding (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set padding(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING, value);
    } else {
      this.removeAttribute(attributes.PADDING);
    }
  }

  get padding() { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the horizontal padding (padding-inline).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingX(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_X, value);
    } else {
      this.removeAttribute(attributes.PADDING_X);
    }
  }

  get paddingX() { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the vertical padding (padding-block).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingY(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_Y, value);
    } else {
      this.removeAttribute(attributes.PADDING_Y);
    }
  }

  get paddingY() { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the margin (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', 'auto', or null
   */
  set margin(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value)) {
      this.setAttribute(attributes.MARGIN, value);
    } else {
      this.removeAttribute(attributes.MARGIN);
    }
  }

  get margin() { return this.getAttribute(attributes.MARGIN); }

  /**
   * Set the vertical margin (margin-block).
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set marginY(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value) && value !== 'auto') {
      this.setAttribute(attributes.MARGIN_Y, value);
    } else {
      this.removeAttribute(attributes.MARGIN_Y);
    }
  }

  get marginY() { return this.getAttribute(attributes.MARGIN_Y); }

  /**
   * Set the height of the flex container
   * @param {string} value The height value (e.g.'200px')
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.style.height = value;
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.height = '';
    }
  }

  get height() { return this.getAttribute(attributes.HEIGHT); }

  /**
   * Set the justify content setting
   * @param {string} value The value
   */
  set justifyContent(value: string | null) {
    if (value && FLEX_OPTIONS.justifyContent.includes(value)) {
      this.setAttribute(attributes.JUSTIFY_CONTENT, value);
    } else {
      this.removeAttribute(attributes.JUSTIFY_CONTENT);
    }
  }

  get justifyContent() { return this.getAttribute(attributes.JUSTIFY_CONTENT); }

  /**
   * Set the wrap setting
   * @param {string} value The value
   */
  set wrap(value: string | null) {
    if (value && FLEX_OPTIONS.wrap.includes(value)) {
      this.setAttribute(attributes.WRAP, value);
    } else {
      this.removeAttribute(attributes.WRAP);
    }
  }

  get wrap() { return this.getAttribute(attributes.WRAP); }

  /**
   * Set flex container to 100% height
   * @param {boolean} val value
   */
  set fullHeight(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.FULL_HEIGHT, '');
    } else {
      this.removeAttribute(attributes.FULL_HEIGHT);
    }
  }

  get fullHeight(): boolean {
    return this.hasAttribute(attributes.FULL_HEIGHT);
  }

  /**
   * Set fill — makes the flex container fill its parent
   * @param {boolean} value The value
   */
  set fill(value: boolean | string | null) {
    this.toggleAttribute(attributes.FILL, stringToBool(value));
  }

  get fill(): boolean {
    return this.hasAttribute(attributes.FILL);
  }
}
